import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import { useEffect, useRef, useCallback, useState, useMemo } from 'react'
import { Box, CircularProgress } from '@mui/material'

const MEDIA_STAGE_HEIGHT = '52vh'

const formatMetaList = (list, emptyLabel) => {
  if (Array.isArray(list) && list.length > 0) {
    return list.join(', ')
  }
  return emptyLabel
}

const getItemPreviewUrl = (item) => {
  if (typeof item === 'string') return item
  if (item?.link) return item.link
  if (Array.isArray(item?.images) && item.images.length > 0) return '__image__'
  return null
}

const getItemImageUrls = (item) => {
  if (!item || typeof item === 'string') return []
  if (Array.isArray(item.images) && item.images.length > 0) return item.images
  if (!item.link && item.thumbnail) return [item.thumbnail]
  return []
}

/**
 * Video / image preview modal. Pass videoSrc + show + onHide for a simple player.
 * Optional videos + currentIndex + onIndexChange for previous/next navigation.
 * Optional previewVideo + callbacks render a footer toolbar (Video Manager).
 */
export const PlayVideoInModal = ({
  show,
  onHide,
  videoSrc,
  videos = null,
  currentIndex = 0,
  onIndexChange = null,
  getVideoUrl = getItemPreviewUrl,
  getVideoLabel = (item, index) =>
    item?.caption || item?.title || `Video ${index + 1}`,
  previewVideo = null,
  activePermission = true,
  onViewDetails,
  onViewUser,
  onViewReports,
  viewUserLabel = 'View user',
  viewReportsLabel = 'View reports',
  onEditLanguage,
  onGiveReward,
  onStatusChange
}) => {
  const videoRef = useRef(null)
  const [mediaLoading, setMediaLoading] = useState(false)
  const [imageIndex, setImageIndex] = useState(0)

  const playlist = Array.isArray(videos) ? videos.filter((v) => getVideoUrl(v)) : []
  const hasPlaylist = playlist.length > 1 && typeof onIndexChange === 'function'
  const safeIndex = Math.min(Math.max(0, currentIndex), Math.max(0, playlist.length - 1))
  const canPrev = hasPlaylist && safeIndex > 0
  const canNext = hasPlaylist && safeIndex < playlist.length - 1

  const imageUrls = useMemo(
    () => getItemImageUrls(previewVideo),
    [previewVideo]
  )
  const showImageGallery = !videoSrc && imageUrls.length > 0
  const canPrevImage = showImageGallery && imageUrls.length > 1 && imageIndex > 0
  const canNextImage = showImageGallery && imageUrls.length > 1 && imageIndex < imageUrls.length - 1

  const categoryText = formatMetaList(previewVideo?.categoryList, 'No category')
  const languageText = formatMetaList(previewVideo?.languageList, 'No language')

  const goPrev = useCallback(() => {
    if (canPrev) onIndexChange(safeIndex - 1)
  }, [canPrev, onIndexChange, safeIndex])

  const goNext = useCallback(() => {
    if (canNext) onIndexChange(safeIndex + 1)
  }, [canNext, onIndexChange, safeIndex])

  useEffect(() => {
    setImageIndex(0)
  }, [previewVideo?.id, videoSrc])

  useEffect(() => {
    if (show && (videoSrc || showImageGallery)) {
      setMediaLoading(true)
    } else if (!show) {
      setMediaLoading(false)
    }
  }, [show, videoSrc, showImageGallery, previewVideo?.id])

  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    if (show && videoSrc) {
      el.load()
      el.play().catch((err) => {
        console.warn('Autoplay blocked:', err)
      })
    } else if (!show) {
      el.pause()
      el.currentTime = 0
    }
  }, [show, videoSrc])

  useEffect(() => {
    if (!show || !hasPlaylist) return
    const onKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        if (showImageGallery && imageUrls.length > 1) {
          setImageIndex((i) => Math.max(0, i - 1))
        } else {
          goPrev()
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        if (showImageGallery && imageUrls.length > 1) {
          setImageIndex((i) => Math.min(imageUrls.length - 1, i + 1))
        } else {
          goNext()
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [show, hasPlaylist, goPrev, goNext, showImageGallery, imageUrls.length])

  const hasActions =
    previewVideo &&
    (onViewDetails ||
      onViewUser ||
      onViewReports ||
      onEditLanguage ||
      onGiveReward ||
      onStatusChange)

  const currentLabel =
    hasPlaylist && playlist[safeIndex]
      ? getVideoLabel(playlist[safeIndex], safeIndex)
      : null

  const showNavArrows = hasPlaylist || (showImageGallery && imageUrls.length > 1)

  const renderMedia = () => {
    if (videoSrc) {
      return (
        <video
          ref={videoRef}
          key={videoSrc}
          controls
          playsInline
          className='w-100 h-100'
          style={{
            objectFit: 'contain',
            background: '#000',
            paddingLeft: showNavArrows ? 48 : 0,
            paddingRight: showNavArrows ? 48 : 0
          }}
          onLoadStart={() => setMediaLoading(true)}
          onWaiting={() => setMediaLoading(true)}
          onCanPlay={() => setMediaLoading(false)}
          onPlaying={() => setMediaLoading(false)}
          onError={() => setMediaLoading(false)}
        >
          <source src={videoSrc} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
      )
    }

    if (showImageGallery) {
      return (
        <img
          key={imageUrls[imageIndex]}
          src={imageUrls[imageIndex]}
          alt=''
          className='h-100'
          style={{
            maxWidth: '100%',
            objectFit: 'contain',
            paddingLeft: showNavArrows ? 48 : 0,
            paddingRight: showNavArrows ? 48 : 0
          }}
          onLoad={() => setMediaLoading(false)}
          onError={() => setMediaLoading(false)}
        />
      )
    }

    return (
      <div className='text-muted text-center px-4'>No video or image to preview</div>
    )
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      enforceFocus={false}
      restoreFocus={false}
      dialogClassName='video-preview-modal-70'
      contentClassName='bg-dark text-light'
    >
      <Modal.Header closeButton closeVariant='white' className='border-secondary flex-shrink-0'>
        <Modal.Title id='video-preview-modal-title' className='text-truncate pe-3'>
          {previewVideo?.profileDetails?.userName
            ? `Preview — ${previewVideo.profileDetails.userName}`
            : currentLabel || 'Preview'}
          {hasPlaylist && (
            <small className='d-block text-white-50 fw-normal mt-1'>
              {safeIndex + 1} of {playlist.length} · Use ← → keys
            </small>
          )}
          {showImageGallery && imageUrls.length > 1 && !hasPlaylist && (
            <small className='d-block text-white-50 fw-normal mt-1'>
              Image {imageIndex + 1} of {imageUrls.length}
            </small>
          )}
        </Modal.Title>
      </Modal.Header>

      {previewVideo && (
        <div className='px-3 py-2 border-bottom border-secondary bg-dark flex-shrink-0'>
          <div className='d-flex flex-wrap gap-3 small'>
            <span className='text-white-50'>
              Category: <span className='text-white'>{categoryText}</span>
            </span>
            <span className='text-white-50'>
              Language: <span className='text-white'>{languageText}</span>
            </span>
          </div>
        </div>
      )}

      <Modal.Body
        className='p-0 d-flex justify-content-center align-items-center bg-black position-relative video-preview-modal-body'
        style={{ minHeight: MEDIA_STAGE_HEIGHT, height: MEDIA_STAGE_HEIGHT }}
      >
        {hasPlaylist && (
          <Button
            variant='outline-light'
            className='position-absolute start-0 top-50 translate-middle-y ms-2 rounded-circle d-flex align-items-center justify-content-center'
            style={{ width: 44, height: 44, zIndex: 4, opacity: canPrev ? 1 : 0.35 }}
            disabled={!canPrev}
            onClick={goPrev}
            aria-label='Previous item'
          >
            ‹
          </Button>
        )}

        {showImageGallery && imageUrls.length > 1 && !hasPlaylist && (
          <Button
            variant='outline-light'
            className='position-absolute start-0 top-50 translate-middle-y ms-2 rounded-circle d-flex align-items-center justify-content-center'
            style={{ width: 44, height: 44, zIndex: 4, opacity: canPrevImage ? 1 : 0.35 }}
            disabled={!canPrevImage}
            onClick={() => setImageIndex((i) => Math.max(0, i - 1))}
            aria-label='Previous image'
          >
            ‹
          </Button>
        )}

        <Box
          className='w-100 h-100 d-flex align-items-center justify-content-center position-relative'
          sx={{ minHeight: MEDIA_STAGE_HEIGHT }}
        >
          {mediaLoading && (
            <Box
              className='position-absolute d-flex flex-column align-items-center justify-content-center'
              sx={{
                inset: 0,
                zIndex: 3,
                bgcolor: 'rgba(0,0,0,0.55)'
              }}
            >
              <CircularProgress size={40} sx={{ color: '#fff' }} />
              <span className='text-white-50 small mt-2'>Loading…</span>
            </Box>
          )}
          {renderMedia()}
        </Box>

        {hasPlaylist && (
          <Button
            variant='outline-light'
            className='position-absolute end-0 top-50 translate-middle-y me-2 rounded-circle d-flex align-items-center justify-content-center'
            style={{ width: 44, height: 44, zIndex: 4, opacity: canNext ? 1 : 0.35 }}
            disabled={!canNext}
            onClick={goNext}
            aria-label='Next item'
          >
            ›
          </Button>
        )}

        {showImageGallery && imageUrls.length > 1 && !hasPlaylist && (
          <Button
            variant='outline-light'
            className='position-absolute end-0 top-50 translate-middle-y me-2 rounded-circle d-flex align-items-center justify-content-center'
            style={{ width: 44, height: 44, zIndex: 4, opacity: canNextImage ? 1 : 0.35 }}
            disabled={!canNextImage}
            onClick={() => setImageIndex((i) => Math.min(imageUrls.length - 1, i + 1))}
            aria-label='Next image'
          >
            ›
          </Button>
        )}
      </Modal.Body>

      <Modal.Footer className='border-secondary bg-dark d-flex flex-column align-items-stretch gap-2 p-3 flex-shrink-0'>
        {hasPlaylist && (
          <div
            className={`d-flex align-items-center justify-content-center gap-3 flex-wrap w-100 ${
              hasActions ? 'pb-2 mb-1 border-bottom border-secondary' : ''
            }`}
          >
            <Button variant='outline-light' disabled={!canPrev} onClick={goPrev}>
              ‹ Previous
            </Button>
            <span className='text-white fw-semibold px-2'>
              {safeIndex + 1} / {playlist.length}
            </span>
            <Button variant='outline-light' disabled={!canNext} onClick={goNext}>
              Next ›
            </Button>
          </div>
        )}

        {hasActions ? (
          <div className='d-flex flex-wrap justify-content-between align-items-center gap-2 w-100'>
            <div className='d-flex flex-wrap align-items-center gap-2'>
              {onViewDetails && (
                <Button
                  variant='outline-light'
                  size='sm'
                  onClick={() => onViewDetails(previewVideo)}
                >
                  View details
                </Button>
              )}
              {onViewUser && (
                <Button
                  variant='outline-light'
                  size='sm'
                  onClick={() => onViewUser(previewVideo)}
                >
                  {viewUserLabel}
                </Button>
              )}
              {onViewReports && (
                <Button
                  variant='outline-info'
                  size='sm'
                  onClick={() => onViewReports(previewVideo)}
                >
                  {viewReportsLabel}
                </Button>
              )}
              {onEditLanguage && (
                <Button
                  variant='outline-light'
                  size='sm'
                  onClick={() => onEditLanguage(previewVideo)}
                >
                  Edit language / category
                </Button>
              )}
              {onGiveReward && !previewVideo?.rewarded && (
                <Button
                  variant='outline-warning'
                  size='sm'
                  onClick={() => onGiveReward(previewVideo)}
                >
                  Give reward
                </Button>
              )}
              {onStatusChange && (
                <Form.Check
                  type='switch'
                  id='preview-video-active-switch'
                  className='text-white ms-2'
                  label='Active'
                  checked={!!previewVideo?.active}
                  disabled={!activePermission}
                  onChange={(e) => {
                    if (activePermission) {
                      onStatusChange(e, previewVideo?.id)
                    }
                  }}
                />
              )}
            </div>
            <Button variant='secondary' onClick={onHide}>
              Close
            </Button>
          </div>
        ) : (
          <div className='w-100 d-flex justify-content-end'>
            <Button variant='outline-light' onClick={onHide}>
              Close
            </Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  )
}
